import * as three from "three";
import {
  getLollipopBaseMesh,
  getSelectionMesh,
  getPetalsMesh,
  drawPetal,
  drawBase,
  drawSelection,
  updateVisPetal,
  getPhasingGroupMesh,
  drawPhasingGroup,
  updateVisBase,
  updatePetalColor,
  updateBaseColor,
} from "./sampleBlueprint";
import pathogenicityTicks from "../pathogenicity/pathogenicityTicks";
import Variant from "../../../../models/Variant";
import Lollipop from "./models/Lollipop";
import LollipopPetal from "./models/LollipopPetal";
import ClinicalSampleVariant from "./models/ClinicalSampleVariant";
import { getSelectionColorMap } from "../../constants/colors";
import getFilters from "./getFilters";
import Filter from "../../../../models/Filter";
import {
  PATHOGENICITY_CL,
  PHASING_CL,
  IMPACT_NULL_VARS_CL,
  IMPACT_OTHER_CL,
} from "../../constants/options";
import PhasingGroup from "./models/PhasingGroup";
import castArray from "../../../../common/helpers/castArray";
import partition from "../../helpers/partition";
import ColorScale from "../../models/ColorScale";
import { impactColorIndex, impactLookup } from "../../constants/pathogenicity";

function getDefaultState(track, sample, otherSamples) {
  const colorByPathogenicity = "Pathogenicity";
  const colorByPhasingGroup = "Phasing Group";
  const colorByImpact = "Coding Impact";
  const options = track.getTrackOptions() || {};
  const selectedColorBy = options.colorBy || colorByImpact;

  return {
    options,
    colorByPathogenicity,
    colorByPhasingGroup,
    colorByImpact,
    selectedColorBy,
    pathogenicityColors: [],
    phasingColors: [],
    impactColors: [],
    sampleDict: [],
    currentSample: sample,
    selectedVariantId: null,
    cnvAreaDict: {},
    cnvOveralapFilterCounter: {},
    loadedPhasingChrSet: new Set(),
    phasingGroups: [],
    phasingDict: new Map(),
    variantDict: {},
    petalsPhased: [],
    petalsUnphased: [],
    basesPhased: [],
    basesUnphased: [],
    selections: [],
    //
    chrToPetalMap: new Map(),
    variantToPetalMap: new Map(),
    filterCounter: new Map(),

    config: null,
    filters: getFilters(
      selectedColorBy,
      otherSamples,
      colorByPathogenicity,
      colorByPhasingGroup,
      colorByImpact
    ),
  };
}

function sampleTrackController(track) {
  const { allSamples, sample: current } = track?.conf?.data || {};
  const otherSamples = allSamples.filter((x) => x.name !== current.name);
  let state = getDefaultState(track, current.name, otherSamples);

  const basePhasedMesh = getLollipopBaseMesh();
  const baseUnphasedMesh = getLollipopBaseMesh();
  const selectionMesh = getSelectionMesh();
  const petalsMeshPhased = getPetalsMesh();
  const petalsMeshUnphased = getPetalsMesh();
  const phasingGroupMesh = getPhasingGroupMesh();

  function onReset() {
    onPreferencesChanged();
    state = getDefaultState(track, current.name, otherSamples);
  }

  function onInit() {
    onPreferencesChanged();
    track.setAxisProps({ ticks: pathogenicityTicks });
    updateColors(true);
  }

  function onRangeChange(props) {
    const loadedPhasingGroupChrSet = state.loadedPhasingChrSet;
    const { chrSegments } = props;
    const { analysisId, hasPhasingGroups } = track.getSharedState();
    const conf = { analysisId, loadedPhasingGroupChrSet, hasPhasingGroups };
    track.loadPosition(chrSegments, conf);
  }

  function getLollipopId(v) {
    return `${v.variant.region.g0}-${v.pathoIndex}`;
  }

  function processSample(sample, variantInfo, chr) {
    const items = sample.variants
      .filter((x) => {
        return x && x.variant_id && !state.variantToPetalMap.get(x.variant_id);
      })
      .map((x) => {
        const shared = variantInfo[x.variant_id];
        const data = { ...x, ...shared };
        const existing = track.variantRegistry.get(data.variant_id);
        const variant = existing || new Variant(data, chr);

        variant.sampleInfo = variant.sampleInfo || {};
        variant.inSamples = shared.inSamples;

        const phasingGroupId = PhasingGroup.getId(chr, x.phasing_group);
        const phasingGroup = state.phasingDict.get(phasingGroupId);
        const sampleVariant = new ClinicalSampleVariant(
          data,
          variant,
          sample,
          phasingGroup
        );
        if (phasingGroup) phasingGroup.variantCount += 1;
        track.variantRegistry.set(variant.id, variant);
        variant.sampleInfo[sample.analysisId] = sampleVariant;
        return sampleVariant;
      });

    const lollipopMap = items.reduce((a, i) => {
      const id = getLollipopId(i);
      const existing = a.get(id) || new Lollipop();
      return a.set(id, existing);
    }, new Map());

    const petalMap = items.reduce((a, i) => {
      const petal = a.get(i.id) || new LollipopPetal();
      const lollipop = lollipopMap.get(getLollipopId(i));
      petal.addVariant(i);
      petal.addToLollipop(lollipop);
      return a.set(i.id, petal);
    }, new Map());

    const petalArr = Array.from(petalMap.values());
    const isPhased = (x) => Boolean(x.detail.phasingGroup);

    const [unphasedArr, phasedArr] = partition(petalArr, isPhased);
    const { petalsUnphased, petalsPhased } = state;
    const colorByPhasing = state.colorByPhasingGroup === state.selectedColorBy;
    const colorByImpact = state.colorByImpact === state.selectedColorBy;

    state.petalsPhased = petalsPhased.concat(phasedArr);
    state.petalsUnphased = petalsUnphased.concat(unphasedArr);

    petalsMeshPhased.setItems(state.petalsPhased);
    petalsMeshUnphased.setItems(state.petalsUnphased);
    basePhasedMesh.setItems(state.petalsPhased);
    baseUnphasedMesh.setItems(state.petalsUnphased);

    updateSelection();
    drawPetal(phasedArr, petalsMeshPhased, colorByPhasing, colorByImpact);
    drawPetal(unphasedArr, petalsMeshUnphased, colorByPhasing, colorByImpact);
    drawBase(phasedArr, basePhasedMesh, colorByPhasing, colorByImpact);
    drawBase(unphasedArr, baseUnphasedMesh, colorByPhasing, colorByImpact);

    petalArr.forEach((petal) => {
      const { zygosity, pathoIndex, codingImpactIndex, variant, tier } =
        petal.detail;

      const { id } = variant;
      const zygKey = `zygosity-${zygosity}`;
      const ciKey = `impact-${codingImpactIndex}`;
      const ampKey = `amp-${tier}`;
      const pathKey = `pathogenicity-${pathoIndex}`;

      const zygVal = state.filterCounter.get(zygKey) || 0;
      const ciVal = state.filterCounter.get(ciKey) || 0;
      const ampVal = state.filterCounter.get(ampKey) || 0;
      const pathVal = state.filterCounter.get(pathKey) || 0;

      for (let i = 0; i < otherSamples.length; i++) {
        const { name } = otherSamples[i];
        const filterKeyAny = `any-sample-${name}`;
        const filterKeyAll = `all-sample-${name}`;
        const filterKeyNot = `not-sample-${name}`;
        const currentValAny = state.filterCounter.get(filterKeyAny) || 0;
        const currentValAll = state.filterCounter.get(filterKeyAll) || 0;
        const currentValNot = state.filterCounter.get(filterKeyNot) || 0;
        const valIn = variant.inSamples.has(name) ? 1 : 0;
        const valOut = 1 - valIn;

        state.filterCounter.set(filterKeyAny, currentValAny + valIn);
        state.filterCounter.set(filterKeyAll, currentValAll + valIn);
        state.filterCounter.set(filterKeyNot, currentValNot + valOut);
      }

      const existingVariants = state.variantToPetalMap.get(id) || [];
      state.filterCounter.set(zygKey, zygVal + 1);
      state.filterCounter.set(ciKey, ciVal + 1);
      state.filterCounter.set(pathKey, pathVal + 1);
      state.filterCounter.set(ampKey, ampVal + 1);
      state.variantToPetalMap.set(id, [...existingVariants, petal]);
    });

    const chrPetals = state.chrToPetalMap.get(chr.id) || [];
    const allPetals = chrPetals.concat(petalArr);

    state.chrToPetalMap.set(chr.id, allPetals);

    const cnvAreasAll = Object.values(state.cnvAreaDict);
    const cnvAreas = cnvAreasAll.filter((x) => x.chr.id === chr.id);

    cnvAreas.forEach((area) => calcCnvOverlap(petalArr, area));

    calcCnvOverlapCounts();
  }

  function processPhasingGroup(chrPhasing, chr) {
    const groups = chrPhasing.phasing_group || [];
    const phasingGroups = groups
      .map((x, ind) => ({ ...x, ind, chr, id: x.phasing_group_id }))
      .map((x) => new PhasingGroup(x));
    phasingGroups.reduce((x, i) => x.set(i.id, i), state.phasingDict);
    state.phasingGroups = state.phasingGroups.concat(phasingGroups);
    phasingGroupMesh.setItems(state.phasingGroups);

    track.setFilters([...state.filters]);
    drawPhasingGroup(phasingGroups, phasingGroupMesh);
  }

  function updatePhasingGroupFilters() {
    const colorByFilter = getColorByOption();
    const hasPhasingGroups = state.phasingGroups.length > 0;
    const options = hasPhasingGroups
      ? [
          state.colorByPathogenicity,
          state.colorByImpact,
          state.colorByPhasingGroup,
        ]
      : [state.colorByPathogenicity, state.colorByImpact];
    colorByFilter.options = options.map((x) => ({ key: x, label: x }));
    if (
      !hasPhasingGroups &&
      state.selectedColorBy === state.colorByPhasingGroup
    ) {
      state.selectedColorBy = state.colorByImpact;
      colorByFilter.value = state.colorByImpact;
      updateColors(true);
      setColorPalettes();
      applyFilters();
    }
  }

  function onDataReceived({ res, chr }) {
    state.loadedPhasingChrSet.add(chr.id);

    const sampleName = track?.conf.data?.sample?.name;
    const { sampleVariants, phasing } = res || {};
    const { variants_info: variants, samples: sampleArr } = sampleVariants;
    const phasingSamples = castArray(phasing);
    const samples = (sampleArr || []).map((x) => ({
      analysisId: x.analysis_id,
      sampleName: x.sample_name,
      url: x.analysis_url,
      variants: x.variants || [],
    }));

    const sampleDict = samples.reduce(
      (a, i) => ({ ...a, [i.analysisId]: i }),
      state.sampleDict
    );

    phasingSamples
      .filter(Boolean)
      .filter((x) => x.sample_name === sampleName)
      .forEach((x) => processPhasingGroup(x, chr));

    samples
      .filter((x) => x.sampleName === sampleName)
      .forEach((sample) => processSample(sample, variants, chr));

    calcFilters();
    updateSelection();
    updatePhasingGroupFilters();
    applyFilters();

    state = { ...state, sampleDict };
    track.requestRender();
  }

  function calcFilters() {
    state.filters.forEach((x) => {
      x.totalCount = state.filterCounter.get(x.key) || 0;
    });
    track.setFilters([...state.filters]);
  }

  function getActiveFilterKeys() {
    return state.filters
      .filter((x) => x.value)
      .map((x) => (x.key === "coverage" ? `${x.key}-${x.value}` : x.key));
  }

  function getColorByOption() {
    return state.filters
      .filter((x) => x && x.value)
      .find((x) => x.key === "colorBy");
  }

  function updateSelection() {
    const keys = getActiveFilterKeys();

    selectionMesh.reset();
    if (!state.selectedVariantId) return;

    state.selections =
      state.variantToPetalMap.get(String(state.selectedVariantId)) || [];
    selectionMesh.setItems(state.selections);

    drawSelection(state.selections, selectionMesh);
    updateVisPetal(state.selections, selectionMesh, keys);

    track.requestRender();
  }

  function appendNewCnvOverlapFilters(newCnvAreas) {
    const category = "CNV Overlap in any Sample";
    const samples = new Set(newCnvAreas.map((x) => x.sample));
    const samplesArr = Array.from(samples.values());
    const sampleSet = state.filters
      .filter((x) => x.category === category)
      .map((x) => x.label)
      .reduce((a, i) => a.add(i), new Set());

    const newFilters = samplesArr
      .filter((x) => !sampleSet.has(x))
      .map((sample) => {
        return new Filter({
          category,
          disableCategoryNum: true,
          key: `overlap-${sample}`,
          label: `${sample}`,
          nestId: `${sample}-cnv-overlap`,
          isVisible: true,
          totalCount: 0,
        });
      });

    const newKeys = new Set(newFilters.map((x) => x.key));
    const restFilters = state.filters.filter((x) => !newKeys.has(x.key));
    state.filters = [...newFilters, ...restFilters];
    track.setFilters([...state.filters]);
  }

  function calcCnvOverlap(variants, cnvArea) {
    const { areaTracker, sample } = cnvArea;
    if (!cnvArea.count) cnvArea.count = 0;

    for (let y = 0; y < variants.length; y++) {
      const det = variants[y].detail;
      const { g0, g1 } = det.variant.region;
      const val = areaTracker.hasOverlap(g0, g1);
      if (val) det.cnvOverlapSampleSet.add(sample);
      if (val) cnvArea.count += 1;
    }
  }

  function calcCnvOverlapCounts() {
    const countSampleDict = {};
    const areas = Object.values(state.cnvAreaDict);

    areas.forEach((x) => {
      const { sample, count } = x;
      const currentCount = countSampleDict[sample] || 0;
      countSampleDict[sample] = currentCount + (count || 0);
    });

    const filterDict = state.filters.reduce(
      (a, i) => a.set(i.key, i),
      new Map()
    );

    Object.entries(countSampleDict).forEach((x) => {
      const [sample, count] = x;
      const filterKey = `overlap-${sample}`;
      const filter = filterDict.get(filterKey);
      state.filterCounter.set(filter.key, count);
    });
  }

  function updateCnvOverlapState(cnvAreas) {
    if (!cnvAreas) return;

    const newCnvAreas = cnvAreas
      .map((x) => ({ ...x }))
      .filter((x) => {
        const existing = state.cnvAreaDict[x.id];
        return !existing || existing.createdAt < x.createdAt;
      });

    for (let i = 0; i < newCnvAreas.length; i++) {
      const cnvArea = newCnvAreas[i];
      const { chr } = cnvArea;
      const { id: chrId } = chr;
      const variants = state.chrToPetalMap.get(chrId) || [];

      cnvArea.count = 0;
      calcCnvOverlap(variants, cnvArea);
      state.cnvAreaDict[cnvArea.id] = cnvArea;
    }

    appendNewCnvOverlapFilters(newCnvAreas);
    calcCnvOverlapCounts();
    track.setFilters([...state.filters]);
    calcFilters();
    track.requestRender();
  }

  function onSharedStateChange() {
    const { selectedVariantId, cnvAreas } = track.getSharedState();

    if (state.selectedVariantId !== selectedVariantId) {
      state.selectedVariantId = selectedVariantId;
      updateSelection();
    }

    if (state.cnvAreas !== cnvAreas) {
      updateCnvOverlapState(cnvAreas);
      state.cnvAreas = cnvAreas;
    }
  }

  function colorByPhasingGroup() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const scaleName = config[PHASING_CL];
    const vec = getSelectionColorMap(isDark);
    const theme = isDark ? 2 : 1;

    const u = isDark
      ? new three.Vector4(0.4, 0.4, 0.5, 0.7)
      : new three.Vector4(0.8, 0.8, 0.8, 1);

    const a = isDark ? 0.7 : 0.8;
    const hexVecs = new ColorScale(scaleName, 8, 11)
      .setTheme(theme)
      .setOpacity(a).vectors;

    const hexMutePetal = new Array(11).fill(u).map((x) => x.clone());
    const hexMuteBase = new Array(11).fill(u).map((x) => x.clone());

    const hexArrPetals = hexVecs.map((x) => x.clone());
    const hexArrBases = hexVecs.map((x) => x.clone());

    selectionMesh.uniforms.setColorMap([vec]);
    phasingGroupMesh.uniforms.setColorMap(hexArrPetals);
    // petals
    petalsMeshPhased.uniforms.setColorMap(hexArrPetals);
    petalsMeshUnphased.uniforms.setColorMap(hexMutePetal);
    // bases
    basePhasedMesh.uniforms.setColorMap(hexArrBases);
    baseUnphasedMesh.uniforms.setColorMap(hexMuteBase);
  }

  function colorByPathogenicity() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const pathoScale = config[PATHOGENICITY_CL];
    const phaseScale = config[PHASING_CL];
    const vec = getSelectionColorMap(isDark);
    const theme = isDark ? 2 : 1;
    const a = isDark ? 0.7 : 0.8;

    const pathoVecs = new ColorScale(pathoScale, 5, 11)
      .setTheme(theme)
      .setOpacity(a).vectors;

    const phaseVecs = new ColorScale(phaseScale, 8, 11)
      .setTheme(theme)
      .setOpacity(a).vectors;

    const baseVecs = pathoVecs.map((x) => x.clone());

    // selection
    selectionMesh.uniforms.setColorMap([vec]);
    // petals
    petalsMeshPhased.uniforms.setColorMap(pathoVecs);
    petalsMeshUnphased.uniforms.setColorMap(pathoVecs);
    // bases
    basePhasedMesh.uniforms.setColorMap(baseVecs);
    baseUnphasedMesh.uniforms.setColorMap(baseVecs);

    // phasing groups
    phasingGroupMesh.uniforms.setColorMap(phaseVecs);
  }

  function colorByCodingImpact() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const nullScale = config[IMPACT_NULL_VARS_CL];
    const otherScale = config[IMPACT_OTHER_CL];
    const theme = isDark ? 2 : 1;

    const vec = getSelectionColorMap(isDark);

    const getGray = () =>
      isDark
        ? new three.Vector4(0.2, 0.2, 0.3, 1)
        : new three.Vector4(0.8, 0.8, 0.85, 1);

    const a = isDark ? 0.7 : 0.8;

    const cScaleNull = new ColorScale(nullScale, 6)
      .setTheme(theme)
      .setOpacity(a);

    const cScaleOther = new ColorScale(otherScale, 4)
      .setTheme(theme)
      .setOpacity(a);

    const { vectors: nullVecs } = cScaleNull;
    const { vectors: otherVecs } = cScaleOther;
    const impactVecs = [getGray()].concat(otherVecs).concat(nullVecs);
    const impactBaseVecs = Array(11).fill(0).map(getGray);

    // selection
    selectionMesh.uniforms.setColorMap([vec]);
    // petals
    petalsMeshPhased.uniforms.setColorMap(impactVecs);
    petalsMeshUnphased.uniforms.setColorMap(impactVecs);
    // bases
    basePhasedMesh.uniforms.setColorMap(impactBaseVecs);
    baseUnphasedMesh.uniforms.setColorMap(impactBaseVecs);

    // phasing groups
    phasingGroupMesh.uniforms.setColorMap(impactVecs);
  }

  function setColorPalettes() {
    if (state.selectedColorBy === state.colorByPhasingGroup) {
      colorByPhasingGroup();
    } else if (state.selectedColorBy === state.colorByImpact) {
      colorByCodingImpact();
    } else {
      colorByPathogenicity();
    }
  }

  function onPreferencesChanged() {
    setColorPalettes();
    const config = track.getPreferences();
    const { isDark } = config;
    const pathoScale = config[PATHOGENICITY_CL];
    const phaseScale = config[PHASING_CL];
    const nullScale = config[IMPACT_NULL_VARS_CL];
    const otherScale = config[IMPACT_OTHER_CL];
    const theme = isDark ? 2 : 1;
    const pathoHex = new ColorScale(pathoScale, 5, 11).setTheme(theme).hexes;
    const phaseHex = new ColorScale(phaseScale, 8, 11).setTheme(theme).hexes;
    const nullHex = new ColorScale(nullScale, 6).setTheme(theme).hexes;
    const otherHex = new ColorScale(otherScale, 4).setTheme(theme).hexes;
    const impactHex = [otherHex[0]].concat(otherHex).concat(nullHex);

    state.pathogenicityColors = pathoHex;
    state.phasingColors = phaseHex;
    state.impactColors = impactHex;

    petalsMeshPhased.material.blending = isDark
      ? three.CustomBlending
      : three.NormalBlending;
    petalsMeshPhased.material.blendEquation = three.AddEquation;
    petalsMeshPhased.material.blendSrc = three.SrcAlphaFactor;
    petalsMeshPhased.material.blendDst = three.OneMinusSrcAlphaFactor;

    petalsMeshUnphased.material.blending = isDark
      ? three.CustomBlending
      : three.NormalBlending;
    petalsMeshUnphased.material.blendEquation = three.AddEquation;
    petalsMeshUnphased.material.blendSrc = three.SrcAlphaFactor;
    petalsMeshUnphased.material.blendDst = three.OneMinusSrcAlphaFactor;

    updateColors();
    track.requestRender();
  }

  function onFiltersChange(updatedFilters) {
    state.filters = updatedFilters;

    setTimeout(() => {
      updateColors();
      track.setFilters([...state.filters]);
      applyFilters();
    }, 50);
  }

  function applyFilters() {
    const keys = getActiveFilterKeys();
    updateVisPetal(state.petalsPhased, petalsMeshPhased, keys);
    updateVisPetal(state.petalsUnphased, petalsMeshUnphased, keys);
    updateVisPetal(state.selections, selectionMesh, keys);
    updateVisBase(state.petalsPhased, basePhasedMesh, keys);
    updateVisBase(state.petalsUnphased, baseUnphasedMesh, keys);
    track.requestRender();
  }

  function updateColors(force) {
    const { value: colorBy } = getColorByOption();
    if (colorBy !== state.selectedColorBy && colorBy !== colorByPhasingGroup) {
      track.setTrackOptions({ ...track.options, colorBy });
    }

    if (colorBy !== state.selectedColorBy || force) {
      state.selectedColorBy = colorBy;
      const { petalsPhased, petalsUnphased } = state;
      const rgbPhase = colorBy === state.colorByPhasingGroup;
      const rgbImpact = colorBy === state.colorByImpact;
      const impactHex = state.impactColors;
      const hasImpHex = state.selectedColorBy === state.colorByImpact;

      state.filters
        .filter((x) => x.key.indexOf("impact-") > -1)
        .forEach((x) => {
          const key = x.key.replace("impact-", "");
          const hex = impactHex[impactColorIndex[impactLookup[key]]];
          x.color = hasImpHex ? hex : null;
        });

      updatePetalColor(petalsPhased, petalsMeshPhased, rgbPhase, rgbImpact);
      updatePetalColor(petalsUnphased, petalsMeshUnphased, rgbPhase, rgbImpact);
      updateBaseColor(petalsPhased, basePhasedMesh, rgbPhase, rgbImpact);
      updateBaseColor(petalsUnphased, baseUnphasedMesh, rgbPhase, rgbImpact);
      setColorPalettes();
      track.setFilters([...state.filters]);
    }
  }

  function getState() {
    return state;
  }

  return {
    getState,
    onReset,
    onPreferencesChanged,
    onInit,
    onRangeChange,
    onSharedStateChange,
    onFiltersChange,
    onDataReceived,
    meshes: [
      baseUnphasedMesh,
      basePhasedMesh,
      phasingGroupMesh,
      petalsMeshUnphased,
      petalsMeshPhased,
      selectionMesh,
    ],
  };
}

export default sampleTrackController;
